import { Injectable, inject, OnDestroy } from '@angular/core';
import { LogBufferService } from './log-buffer.service';
import { SentryService } from './sentry.service';
import { environment } from 'src/environments/environment';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';

type ContextProvider = {
  /** Function that returns context properties */
  provider: () => Record<string, any>;
  /** Optional condition to determine if this context should be included */
  condition?: (message: string) => boolean;
};

@Injectable({
  providedIn: 'root',
})
export class LoggerService implements OnDestroy {
  private readonly isProduction: boolean;
  private callStore = inject(CallStoreService, { optional: true });
  private fileContextProperties: Record<string, any> = {};
  private contextProviders: ContextProvider[] = [];

  constructor(
    private logBuffer: LogBufferService,
    private sentry: SentryService
  ) {
    this.isProduction = environment.production;
    this.initializeContextProviders();
  }

  /**
   * Initialize built-in context providers.
   * Call-related logs automatically include global call properties from callStore.
   */
  private initializeContextProviders(): void {
    // Register call-related context provider
    this.contextProviders.push({
      provider: () => this.callStore?.getGlobalLogProperties() || {},
      condition: (message: string) => message.startsWith('[CALL]'),
    });

    // Future: Add more context providers here
    // Example:
    // this.contextProviders.push({
    //   provider: () => this.authStore?.getAuthContext() || {},
    //   condition: (message: string) => message.startsWith('[AUTH]')
    // });
  }

  /**
   * Set file-level context properties that will be automatically included in all logs.
   * Use this to set properties once per file (e.g., conversationId) instead of passing them to every log call.
   *
   * @param properties - Properties to merge with all logs
   * @example
   * // In your service constructor or init method:
   * this.logger.setFileContext({ conversationId: this.conversationId });
   */
  setFileContext(properties: Record<string, any>): void {
    this.fileContextProperties = {
      ...this.fileContextProperties,
      ...properties,
    };
  }

  /**
   * Clear specific file-level context properties by key.
   *
   * @param keys - Keys to remove from file context
   */
  clearFileContext(...keys: string[]): void {
    if (keys.length === 0) {
      this.fileContextProperties = {};
    } else {
      keys.forEach((key) => delete this.fileContextProperties[key]);
    }
  }

  private mergeWithGlobalProperties(properties?: Record<string, any>) {
    const globalProps = this.callStore?.getGlobalLogProperties() || {};
    // Merge order: global < file-level < call-specific < timestamp
    return {
      ...globalProps,
      ...this.fileContextProperties,
      ...properties,
      timestamp: Date.now() / 1000,
    };
  }

  log(message: string, properties?: Record<string, any>): void {
    const mergedProps = this.mergeWithGlobalProperties(properties);
    console.log(`[INFO] ${message}`, { data: mergedProps });
    this.logBuffer.enqueue({ message, level: 'info', metaData: mergedProps });
  }

  warn(message: string, properties?: Record<string, any>): void {
    const mergedProps = this.mergeWithGlobalProperties(properties);
    console.warn(`[WARN] ${message}`, { data: mergedProps });
    this.logBuffer.enqueue({ message, level: 'warn', metaData: mergedProps });
  }

  error(message: string, error?: any, properties?: Record<string, any>, tags?: Record<string, string>): void {
    const mergedProps = this.mergeWithGlobalProperties({ ...properties, error });
    // console.error only in dev — in production captureConsoleIntegration(['error']) is active,
    // so console.error would duplicate the explicit captureException below
    if (!this.isProduction) {
      console.error(`[ERROR] ${message}`, error || '', { data: mergedProps });
    }
    this.logBuffer.enqueue({ message, level: 'error', metaData: mergedProps });
    this.logBuffer.flush();
    if (error) this.sentry.captureException(error, {
      ...this.callStore?.getGlobalLogProperties(),
      ...properties,
    }, tags);
  }

  debug(message: string, properties?: Record<string, any>): void {
    if (!this.isProduction) {
      console.debug(`[DEBUG] ${message}`, properties || '');
      this.logBuffer.enqueue({ message, level: 'debug', metaData: properties });
    }
  }

  setUser(userId: string, additionalData?: Record<string, any>) {
    this.sentry.setUser(userId, additionalData);
    this.debug('User context set', { userId });
  }

  clearUser() {
    this.sentry.clearUser();
    this.debug('User context cleared');
  }

  setContext(key: string, value: Record<string, any>) {
    this.sentry.setContext(key, value);
  }

  setTag(key: string, value: string | number | boolean) {
    this.sentry.setTag(key, value);
  }

  ngOnDestroy() {
    this.logBuffer.ngOnDestroy();
  }
}
